import {
  FieldPredicate,
  makeFieldIn,
  makeFieldLike,
  makeTableQuery,
  SortDirection,
  SortField,
} from "../../../adl-gen/common/tabular";
import { WithDbId } from "../../../adl-gen/common/db";
import { AdminService } from "./service";

export const PLACEMENT_PUBLISHER_NAME_FIELD = "publisher_name";
const PLACEMENT_PUBLISHER_ID_FIELD = "publisher_id";

function stripLikePattern(pattern: string): string {
  let value = pattern;
  if (value.startsWith("%")) {
    value = value.slice(1);
  }
  if (value.endsWith("%")) {
    value = value.slice(0, -1);
  }
  return value;
}

function isPublisherNameLike(
  filter: FieldPredicate,
  field: string
): filter is FieldPredicate & {
  kind: "like";
  value: { field: { kind: "name"; value: string }; pattern: string };
} {
  return (
    filter.kind === "like" &&
    filter.value.field.kind === "name" &&
    filter.value.field.value === field
  );
}

function extractPublisherNamePattern(
  filter: FieldPredicate,
  field: string
): string {
  if (isPublisherNameLike(filter, field)) {
    return stripLikePattern(filter.value.pattern);
  }
  if (filter.kind === "and") {
    for (const clause of filter.value) {
      const pattern = extractPublisherNamePattern(clause, field);
      if (pattern !== "") {
        return pattern;
      }
    }
  }
  return "";
}

function removeFieldLikeFilter(
  filter: FieldPredicate,
  field: string
): FieldPredicate {
  if (isPublisherNameLike(filter, field)) {
    return { kind: "literal", value: true };
  }
  if (filter.kind === "and") {
    const remaining = filter.value
      .map((clause) => removeFieldLikeFilter(clause, field))
      .filter(
        (clause) => !(clause.kind === "literal" && clause.value === true)
      );
    if (remaining.length === 0) {
      return { kind: "literal", value: true };
    }
    if (remaining.length === 1) {
      return remaining[0];
    }
    return { kind: "and", value: remaining };
  }
  if (filter.kind === "or") {
    return {
      kind: "or",
      value: filter.value.map((clause) => removeFieldLikeFilter(clause, field)),
    };
  }
  if (filter.kind === "not") {
    return { kind: "not", value: removeFieldLikeFilter(filter.value, field) };
  }
  return filter;
}

function combineFilters(
  left: FieldPredicate,
  right: FieldPredicate
): FieldPredicate {
  if (left.kind === "literal" && left.value === true) {
    return right;
  }
  if (right.kind === "literal" && right.value === true) {
    return left;
  }
  if (left.kind === "literal" && left.value === false) {
    return left;
  }
  if (right.kind === "literal" && right.value === false) {
    return right;
  }
  if (left.kind === "and") {
    return { kind: "and", value: [...left.value, right] };
  }
  return { kind: "and", value: [left, right] };
}

function escapeLikePattern(pattern: string): string {
  return pattern.replace(/[%\\_]/g, "\\$&");
}

export function getPlacementPublisherNameFilter(
  filter: FieldPredicate
): string {
  return extractPublisherNamePattern(filter, PLACEMENT_PUBLISHER_NAME_FIELD);
}

export function resolvePlacementTableSorting(sorting: SortField[]): {
  apiSorting: SortField[];
  publisherNameSorting: SortField[];
} {
  const publisherNameSorting = sorting.filter(
    (sortField) => sortField.field === PLACEMENT_PUBLISHER_NAME_FIELD
  );
  const apiSorting = sorting.filter(
    (sortField) => sortField.field !== PLACEMENT_PUBLISHER_NAME_FIELD
  );
  return { apiSorting, publisherNameSorting };
}

export function sortPlacementRowsByPublisherName(
  rows: WithDbId<Record<string, unknown>>[],
  publisherNameSorting: SortField[],
  getPublisherName: (publisherId: string) => string
): WithDbId<Record<string, unknown>>[] {
  if (publisherNameSorting.length === 0) {
    return rows;
  }

  const sorted = [...rows];
  sorted.sort((rowA, rowB) => {
    for (const sortField of publisherNameSorting) {
      const publisherIdA = getPlacementPublisherId(rowA.value) ?? "";
      const publisherIdB = getPlacementPublisherId(rowB.value) ?? "";
      const nameA = getPublisherName(publisherIdA);
      const nameB = getPublisherName(publisherIdB);
      const comparison = nameA.localeCompare(nameB, undefined, {
        sensitivity: "base",
      });
      if (comparison !== 0) {
        return sortField.direction === SortDirection.descending
          ? -comparison
          : comparison;
      }
    }
    return 0;
  });
  return sorted;
}

export function getPlacementPublisherId(
  row: Record<string, unknown>
): string | undefined {
  const publisherId = row.publisherId ?? row.publisher_id;
  return typeof publisherId === "string" && publisherId
    ? publisherId
    : undefined;
}

async function queryPublisherIdsByNamePattern(
  service: AdminService,
  pattern: string
): Promise<string[]> {
  const publisherIds: string[] = [];
  const batchSize = 500;
  let offset = 0;
  let totalSize = 0;

  do {
    const dbrows = await service.adminQuery({
      table: "publisher",
      columns: [],
      query: makeTableQuery({
        filter: {
          kind: "like",
          value: makeFieldLike({
            field: { kind: "name", value: "name" },
            pattern: `%${escapeLikePattern(pattern)}%`,
            caseSensitive: false,
          }),
        },
        offset,
        count: batchSize,
      }),
    });

    for (const row of dbrows.items) {
      publisherIds.push(row.id);
    }

    totalSize = dbrows.total_size;
    offset += dbrows.items.length;
    if (dbrows.items.length === 0) {
      break;
    }
  } while (offset < totalSize);

  return publisherIds;
}

export async function resolvePlacementTableFilter(
  service: AdminService,
  filter: FieldPredicate
): Promise<FieldPredicate> {
  const publisherNamePattern = extractPublisherNamePattern(
    filter,
    PLACEMENT_PUBLISHER_NAME_FIELD
  );
  if (publisherNamePattern === "") {
    return filter;
  }

  const withoutPublisherName = removeFieldLikeFilter(
    filter,
    PLACEMENT_PUBLISHER_NAME_FIELD
  );
  const publisherIds = await queryPublisherIdsByNamePattern(
    service,
    publisherNamePattern
  );

  if (publisherIds.length === 0) {
    return combineFilters(withoutPublisherName, {
      kind: "literal",
      value: false,
    });
  }

  return combineFilters(withoutPublisherName, {
    kind: "in",
    value: makeFieldIn({
      field: PLACEMENT_PUBLISHER_ID_FIELD,
      values: publisherIds,
    }),
  });
}
